/**
 * SiteVisitFieldNotes - Enhanced Field Notes Drawer for Site Visits
 * 
 * Sections:
 * 1. Notes (Internal + Client, with voice recordings)
 * 2. Client Expectations
 * 3. Measurements (structured, service-type specific)
 * 4. Photos (with tags)
 * 5. Site Conditions (checkboxes with optional notes)
 * 6. Checklist (required before marking complete)
 * 
 * Features:
 * - Swipe right to dismiss (mobile-friendly for field workers)
 * - Save button in header on mobile
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSwipeToDismiss } from '@/hooks/useSwipeToDismiss';
import { Button } from '@/components/ui/button';
import SaveStatusBadge from '@/components/SaveStatusBadge';
import { SAVE_STATUS } from '@/hooks/useFieldNotesSave';
import CameraCaptureModal from '@/components/CameraCaptureModal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

/**
 * DebouncedTextarea — stores value locally for instant typing, debounces parent updates.
 * This prevents the parent component from re-rendering on every keystroke.
 */
const DebouncedTextarea = React.memo(({ value, onChange, delay = 300, ...props }) => {
  const [localValue, setLocalValue] = useState(value || '');
  const timerRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Sync from parent when value changes externally (e.g., drawer re-open)
  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const handleChange = useCallback((e) => {
    const newVal = e.target.value;
    setLocalValue(newVal); // Instant local update (no parent re-render)
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onChangeRef.current?.(newVal);
    }, delay);
  }, [delay]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return <Textarea {...props} value={localValue} onChange={handleChange} />;
});
DebouncedTextarea.displayName = 'DebouncedTextarea';

/**
 * DebouncedInput — same pattern for <Input> elements.
 */
const DebouncedInput = React.memo(({ value, onChange, delay = 300, multiline = false, rows = 1, ...props }) => {
  const [localValue, setLocalValue] = useState(value || '');
  const timerRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const taRef = useRef(null);
  onChangeRef.current = onChange;

  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  // Auto-grow textarea height
  useEffect(() => {
    if (multiline && taRef.current) {
      taRef.current.style.height = 'auto';
      taRef.current.style.height = `${taRef.current.scrollHeight}px`;
    }
  }, [localValue, multiline]);

  const handleChange = useCallback((e) => {
    const newVal = e.target.value;
    setLocalValue(newVal);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onChangeRef.current?.(newVal);
    }, delay);
  }, [delay]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  if (multiline) {
    return (
      <textarea
        ref={taRef}
        rows={rows}
        {...props}
        value={localValue}
        onChange={handleChange}
      />
    );
  }
  return <Input {...props} value={localValue} onChange={handleChange} />;
});
DebouncedInput.displayName = 'DebouncedInput';

// iOS-safe textarea wrapper - ensures keyboard appears on tap (FIX 2: iPad keyboard)
const MobileTextarea = ({ onTouchEnd, ...props }) => {
  const ref = useRef(null);
  const handleTouchEnd = useCallback((e) => {
    // On iOS, force focus after touch to ensure keyboard appears
    if (ref.current) {
      setTimeout(() => ref.current?.focus(), 50);
    }
    onTouchEnd?.(e);
  }, [onTouchEnd]);
  
  return (
    <Textarea
      ref={ref}
      onTouchEnd={handleTouchEnd}
      autoComplete="off"
      autoCorrect="on"
      {...props}
    />
  );
};
import { Badge } from '@/components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  X, 
  Plus, 
  Trash2, 
  Check,
  Save,
  Mic,
  MicOff,
  Square,
  Play,
  Pause,
  Camera,
  ImagePlus,
  Edit3,
  StickyNote,
  ClipboardCheck,
  Ruler,
  AlertTriangle,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  Users,
  Download,
  Pen,
  Eraser,
  Undo
} from 'lucide-react';
import { getNichePreset, getMergedNichePreset, NICHE_TO_SERVICE_KEY } from '@/lib/nichePresets';

// Service-type specific measurement OBJECTS — fill any combo of length/width/area/count
const MEASUREMENT_SUGGESTIONS = {
  'Concrete': [
    'Driveway',
    'Patio',
    'Sidewalk',
    'Slab',
    'Foundation',
    'Existing Crack',
    'Curb Length'
  ],
  'Landscaping': [
    'Lawn',
    'Front Yard',
    'Back Yard',
    'Garden Bed',
    'Mulch Area',
    'Tree Count',
    'Shrub Count',
    'Fence Line',
    'Irrigation Zones'
  ],
  'Lawn Care': [
    'Front Yard',
    'Back Yard',
    'Side Yard',
    'Total Lawn',
    'Garden Beds',
    'Mulch Area',
    'Hedges Length',
    'Tree Count',
    'Trim Edging Length'
  ],
  'Detailing': [
    'Vehicle (Sedan)',
    'Vehicle (SUV)',
    'Vehicle (Truck)',
    'Vehicle (Van)',
    'Vehicle (Motorcycle)',
    'Vehicle (RV/Boat)',
    'Engine Bay',
    'Wheels (Count)'
  ],
  'Roofing': [
    'Roof',
    'Roof Pitch',
    'Ridge',
    'Valley Count',
    'Chimney',
    'Gutter',
    'Fascia'
  ],
  'Fencing': [
    'Total Fence',
    'Gate',
    'Posts (Count)',
    'Corners (Count)',
    'Fence Height',
    'Existing Fence'
  ],
  'Decking': [
    'Deck',
    'Railing',
    'Stairs (Count)',
    'Posts (Count)',
    'Joists (Count)'
  ],
  'Painting': [
    'Walls',
    'Ceilings',
    'Trim',
    'Doors (Count)',
    'Windows (Count)',
    'Exterior Siding'
  ],
  'Plumbing': [
    'Pipe Run',
    'Fixtures (Count)',
    'Water Heater',
    'Drain'
  ],
  'Electrical': [
    'Circuits (Count)',
    'Outlets (Count)',
    'Switches (Count)',
    'Panel Capacity',
    'Wire Run'
  ],
  'HVAC': [
    'Square Footage',
    'Ducts',
    'Vents (Count)',
    'Unit Tonnage'
  ],
  'Cleaning': [
    'Total Area',
    'Bedrooms (Count)',
    'Bathrooms (Count)',
    'Kitchen',
    'Living Areas',
    'Windows (Count)'
  ],
  'General': [
    'Area',
    'Length',
    'Width',
    'Height',
    'Depth',
    'Quantity'
  ]
};

const MEASUREMENT_UNITS = [
  { value: 'ft', label: 'ft' },
  { value: 'in', label: 'in' },
  { value: 'sqft', label: 'sq ft' },
  { value: 'linear_ft', label: 'linear ft' },
  { value: 'm', label: 'm' },
  { value: 'sqm', label: 'sq m' },
  { value: 'each', label: 'each' },
  { value: 'count', label: 'count' }
];

const BUDGET_RANGES = [
  { value: '', label: 'Not discussed' },
  { value: 'under_1k', label: 'Under $1,000' },
  { value: '1k_5k', label: '$1,000 - $5,000' },
  { value: '5k_10k', label: '$5,000 - $10,000' },
  { value: '10k_25k', label: '$10,000 - $25,000' },
  { value: '25k_50k', label: '$25,000 - $50,000' },
  { value: '50k_100k', label: '$50,000 - $100,000' },
  { value: 'over_100k', label: 'Over $100,000' }
];

const PHOTO_TAGS = [
  { value: 'before', label: 'Before', color: 'bg-blue-100 text-blue-700' },
  { value: 'damage', label: 'Damage', color: 'bg-red-100 text-red-700' },
  { value: 'measurement', label: 'Measurement', color: 'bg-purple-100 text-purple-700' },
  { value: 'reference', label: 'Reference', color: 'bg-green-100 text-green-700' },
  { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-700' }
];

const DEFAULT_SITE_CONDITIONS = [
  { id: 'limited_access', label: 'Limited equipment access', checked: false, notes: '' },
  { id: 'demolition', label: 'Requires demolition', checked: false, notes: '' },
  { id: 'structure_removal', label: 'Existing structure removal', checked: false, notes: '' },
  { id: 'uneven_terrain', label: 'Uneven terrain', checked: false, notes: '' },
  { id: 'utility_lines', label: 'Utility lines present', checked: false, notes: '' },
  { id: 'permit_required', label: 'Permit required', checked: false, notes: '' }
];

const DEFAULT_CHECKLIST = [
  { id: 'site_access', text: 'Site access confirmed', checked: false },
  { id: 'measurements', text: 'Measurements taken', checked: false },
  { id: 'photos', text: 'Photos captured', checked: false },
  { id: 'customer_requirements', text: 'Customer requirements discussed', checked: false }
];

// Voice Recording Hook with Audio Storage
const useVoiceRecording = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordings, setRecordings] = useState([]);
  const [isSupported, setIsSupported] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recognitionRef = useRef(null);
  const currentTranscriptRef = useRef('');

  useEffect(() => {
    // Check for MediaRecorder support
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      setIsSupported(true);
    }

    // Setup speech recognition for transcription
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        let transcript = '';
        for (let i = 0; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        currentTranscriptRef.current = transcript;
      };
    }

    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];
      currentTranscriptRef.current = '';

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        const newRecording = {
          id: Date.now(),
          url,
          blob,
          transcript: currentTranscriptRef.current || '',
          timestamp: new Date().toISOString(),
          duration: 0
        };
        setRecordings(prev => [...prev, newRecording]);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      
      // Start speech recognition for transcription
      if (recognitionRef.current) {
        try {
          recognitionRef.current.start();
        } catch (e) {
          console.log('Speech recognition already started or not available');
        }
      }

      setIsRecording(true);
      toast.success('Recording started', { id: 'recording-start' });
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Could not access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsRecording(false);
      toast.success('Recording saved', { id: 'recording-stop' });
    }
  };

  const deleteRecording = (id) => {
    setRecordings(prev => {
      const recording = prev.find(r => r.id === id);
      if (recording?.url) {
        URL.revokeObjectURL(recording.url);
      }
      return prev.filter(r => r.id !== id);
    });
  };

  return {
    isRecording,
    recordings,
    isSupported,
    startRecording,
    stopRecording,
    deleteRecording,
    setRecordings
  };
};

// Audio Player Component
const AudioPlayer = ({ recording, onDelete }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
      <audio 
        ref={audioRef} 
        src={recording.url} 
        onEnded={() => setIsPlaying(false)}
        className="hidden"
      />
      <Button
        variant="outline"
        size="icon"
        onClick={togglePlay}
        className="h-10 w-10 rounded-full flex-shrink-0"
      >
        {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      </Button>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground">
          {new Date(recording.timestamp).toLocaleTimeString()}
        </p>
        {recording.transcript && (
          <p className="text-sm mt-1 text-foreground">{recording.transcript}</p>
        )}
        {!recording.transcript && (
          <p className="text-sm mt-1 text-muted-foreground italic">No transcription available</p>
        )}
      </div>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => onDelete(recording.id)}
        className="text-muted-foreground hover:text-destructive flex-shrink-0"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
};

// Collapsible Section Component
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = true, badge }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-primary" />
          <span className="font-semibold text-foreground">{title}</span>
          {badge && <Badge variant="secondary" className="ml-2">{badge}</Badge>}
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>
      {isOpen && <div className="p-4 space-y-4">{children}</div>}
    </div>
  );
};

// Simple Drawing Canvas Component for Field Notes
const FieldNotesSketchPad = ({ sketches = [], onSave, onDelete }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushSize, setBrushSize] = useState(4);
  const [brushColor, setBrushColor] = useState('#1e3a8a');
  const [tool, setTool] = useState('pen');
  const lastPoint = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const coords = getCoordinates(e);
    lastPoint.current = coords;
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
    ctx.lineTo(coords.x, coords.y);
    ctx.strokeStyle = tool === 'eraser' ? '#ffffff' : brushColor;
    ctx.lineWidth = tool === 'eraser' ? brushSize * 3 : brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    lastPoint.current = coords;
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    lastPoint.current = null;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const saveSketch = () => {
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    onSave(dataUrl);
    clearCanvas();
    toast.success('Sketch saved');
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 rounded-lg">
        <Button
          variant={tool === 'pen' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setTool('pen')}
          className="h-9"
          data-testid="sketch-pen-tool"
        >
          <Pen className="w-4 h-4 mr-1" />
          Draw
        </Button>
        <Button
          variant={tool === 'eraser' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setTool('eraser')}
          className="h-9"
          data-testid="sketch-eraser-tool"
        >
          <Eraser className="w-4 h-4 mr-1" />
          Erase
        </Button>
        
        <div className="flex items-center gap-2 ml-1">
          <span className="text-xs text-muted-foreground">Size:</span>
          <input
            type="range"
            min="2"
            max="20"
            value={brushSize}
            onChange={(e) => setBrushSize(parseInt(e.target.value))}
            className="w-16"
          />
        </div>
        
        <div className="flex items-center gap-1 ml-1">
          {['#1e3a8a', '#dc2626', '#16a34a', '#000000'].map((c) => (
            <button
              key={c}
              onClick={() => setBrushColor(c)}
              className={`w-7 h-7 rounded-full border-2 ${brushColor === c ? 'border-gray-800 scale-110' : 'border-gray-300'}`}
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
        
        <div className="flex gap-2 ml-auto">
          <Button variant="outline" size="sm" onClick={clearCanvas} className="h-9">
            <Undo className="w-4 h-4 mr-1" />
            Clear
          </Button>
          <Button onClick={saveSketch} size="sm" className="h-9 bg-green-600 hover:bg-green-700" data-testid="save-sketch-btn">
            <Save className="w-4 h-4 mr-1" />
            Save Sketch
          </Button>
        </div>
      </div>
      
      {/* Canvas */}
      <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white touch-none">
        <canvas
          ref={canvasRef}
          width={800}
          height={400}
          className="w-full cursor-crosshair"
          style={{ touchAction: 'none' }}
          data-testid="sketch-canvas"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
      </div>
      
      {/* Saved Sketches */}
      {sketches.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Saved Sketches ({sketches.length})</Label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {sketches.map((sketch, index) => {
              const sketchUrl = typeof sketch === 'string' ? sketch : (sketch.dataUrl || sketch.url || '');
              return (
                <div key={index} className="relative group">
                  {sketchUrl && (
                    <img 
                      src={sketchUrl} 
                      alt={`Sketch ${index + 1}`}
                      className="w-full h-32 object-contain rounded-lg border border-gray-200 bg-gray-50"
                    />
                  )}
                  <button
                    onClick={() => onDelete(index)}
                    className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex items-center justify-center shadow-sm"
                    data-testid={`delete-sketch-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Auto-save interval in milliseconds (20 seconds on good connection, local-only on bad)
const AUTO_SAVE_INTERVAL = 20000;
// Fetch timeout for auto-save API calls (5 seconds — abort on slow connections)
const FETCH_TIMEOUT_MS = 5000;

// Zero-rerender textarea: value lives in a ref, not state. 
// The parent never re-renders from typing.
const UncontrolledTextarea = React.memo(({ defaultValue, onChangeRef, placeholder, className, testId }) => {
  const ref = useRef(null);
  
  useEffect(() => {
    if (ref.current && ref.current.value !== defaultValue) {
      ref.current.value = defaultValue || '';
    }
  }, [defaultValue]);

  const handleInput = useCallback(() => {
    if (onChangeRef.current) {
      onChangeRef.current(ref.current.value);
    }
  }, [onChangeRef]);

  // iOS keyboard fix: force focus on touch
  const handleTouchEnd = useCallback(() => {
    setTimeout(() => ref.current?.focus(), 50);
  }, []);

  return (
    <textarea
      ref={ref}
      defaultValue={defaultValue || ''}
      onInput={handleInput}
      onTouchEnd={handleTouchEnd}
      placeholder={placeholder}
      className={cn(
        "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      autoComplete="off"
      autoCorrect="on"
      data-testid={testId}
    />
  );
});
UncontrolledTextarea.displayName = 'UncontrolledTextarea';

// Check if current network connection is slow
function isSlowConnection() {
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!conn) return false; // Can't detect — assume good
  // effectiveType: 'slow-2g', '2g', '3g', '4g'
  if (conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g') return true;
  // downlink in Mbps — below 0.5 is very slow
  if (conn.downlink !== undefined && conn.downlink < 0.5) return true;
  return false;
}

// Main Component
const SiteVisitFieldNotes = ({ 
  isOpen, 
  onClose, 
  onSave,
  onAutoSave,
  initialData = {},
  serviceType = 'General',
  businessType = null,  // org.business_type — drives niche presets (PRIMARY)
  secondaryNiches = [],  // org.secondary_niches — additional niche tags
  siteVisitId = null,
  orgId = null,
  mode = 'drawer',  // 'drawer' (modal) or 'page' (inline full-page)
  // Save state machine props (from useFieldNotesSave hook). When provided,
  // the editor renders the SaveStatusBadge in its header so the user always
  // sees the current save state.
  saveStatus = null,
  saveLastSavedAt = null,
  saveLastError = null,
  onRetrySave = null,
  developerMode = false,
  // When provided, renders a secondary "Mark Site Visit Complete" button in
  // the footer. The handler is called AFTER notes are saved successfully.
  onMarkComplete = null,
  // Server-side completion status of the site visit. When true, the editor
  // shows a "Completed" banner and the Mark Complete button label flips to
  // "Re-snapshot" (Phase 3 behaviour: edits don't overwrite the snapshot).
  isCompleted = false,
}) => {
  // Guard against null/undefined initialData (e.g., when component mounts before parent loads data)
  const safeInitialData = initialData || {};
  // Resolve niche presets (memoized) — primary + secondary merged
  const nichePreset = useMemo(
    () => getMergedNichePreset(businessType || serviceType, secondaryNiches || []),
    [businessType, serviceType, secondaryNiches]
  );
  // Text notes stored in REFS — typing causes zero re-renders
  const internalNotesRef = useRef(safeInitialData.internalNotes || '');
  const clientNotesRef = useRef(safeInitialData.clientNotes || '');
  // Stable callback refs for the uncontrolled textareas
  const onInternalNotesChange = useRef((val) => { internalNotesRef.current = val; hasUnsavedChangesRef.current = true; });
  const onClientNotesChange = useRef((val) => { clientNotesRef.current = val; hasUnsavedChangesRef.current = true; });
  
  // Counter to force UncontrolledTextarea remount when drawer re-opens with fresh data
  const [reopenKey, setReopenKey] = useState(0);
  
  // Auto-save state — minimal state to avoid re-renders
  const [lastAutoSaved, setLastAutoSaved] = useState(null);
  const [lastServerSync, setLastServerSync] = useState(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const hasUnsavedChangesRef = useRef(false);
  const isSavingRef = useRef(false); // Guard against overlapping saves
  const abortControllerRef = useRef(null); // For cancelling in-flight fetches
  const initialDataRef = useRef(safeInitialData);
  const prevIsOpenRef = useRef(false);
  
  // Voice recordings
  const internalVoice = useVoiceRecording();
  const clientVoice = useVoiceRecording();
  
  // Client expectations
  const [clientExpectations, setClientExpectations] = useState(safeInitialData.clientExpectations || {
    wantsDone: '',
    timeline: '',
    budgetRange: '',
    specialRequests: ''
  });
  
  // Measurements - use empty array only if actually empty
  const [measurements, setMeasurements] = useState(
    safeInitialData.measurements?.length > 0 ? safeInitialData.measurements : []
  );
  
  // Photos
  const [photos, setPhotos] = useState(
    safeInitialData.photos?.length > 0 ? safeInitialData.photos : []
  );
  const fileInputRef = useRef(null);
  const uploadInputRef = useRef(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  
  // Sketches
  const [sketches, setSketches] = useState(
    safeInitialData.sketches?.length > 0 ? safeInitialData.sketches : []
  );
  
  // Site conditions - use niche-aware defaults if empty array
  const [siteConditions, setSiteConditions] = useState(
    safeInitialData.siteConditions?.length > 0 
      ? safeInitialData.siteConditions 
      : nichePreset.siteConditions.map(c => ({ ...c, checked: false, notes: '' }))
  );
  const [customConditions, setCustomConditions] = useState(safeInitialData.customConditions || []);
  
  // Checklist - use niche-aware defaults if empty array
  const [checklist, setChecklist] = useState(
    safeInitialData.checklist?.length > 0 
      ? safeInitialData.checklist 
      : nichePreset.checklist.map(c => ({ ...c }))
  );

  // Scroll-to-top FAB visibility (page mode only)
  const [showScrollTop, setShowScrollTop] = useState(false);
  useEffect(() => {
    if (mode !== 'page') return;
    const onScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [mode]);

  // Initialize voice recordings from initial data
  useEffect(() => {
    if (safeInitialData.internalVoiceRecordings) {
      internalVoice.setRecordings(safeInitialData.internalVoiceRecordings);
    }
    if (safeInitialData.clientVoiceRecordings) {
      clientVoice.setRecordings(safeInitialData.clientVoiceRecordings);
    }
  }, []);

  // FIX #3: Sync ALL refs and state from fresh initialData when drawer re-opens
  // This prevents stale data from appearing after close → save → re-open
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;
    
    // Only reset on close→open transition (not on every render)
    if (isOpen && !wasOpen) {
      // Sync text refs with latest prop data
      internalNotesRef.current = safeInitialData.internalNotes || '';
      clientNotesRef.current = safeInitialData.clientNotes || '';
      initialDataRef.current = safeInitialData;
      
      // Increment key to force UncontrolledTextarea remount with fresh defaultValue
      setReopenKey(k => k + 1);
      
      // Sync all other state
      setClientExpectations(safeInitialData.clientExpectations || {
        wantsDone: '', timeline: '', budgetRange: '', specialRequests: ''
      });
      setMeasurements(safeInitialData.measurements?.length > 0 ? safeInitialData.measurements : []);
      setPhotos(safeInitialData.photos?.length > 0 ? safeInitialData.photos : []);
      setSketches(safeInitialData.sketches?.length > 0 ? safeInitialData.sketches : []);
      setSiteConditions(
        safeInitialData.siteConditions?.length > 0 
          ? safeInitialData.siteConditions 
          : nichePreset.siteConditions.map(c => ({ ...c, checked: false, notes: '' }))
      );
      setCustomConditions(safeInitialData.customConditions || []);
      setChecklist(
        safeInitialData.checklist?.length > 0 
          ? safeInitialData.checklist 
          : nichePreset.checklist.map(c => ({ ...c }))
      );
      
      // Sync voice recordings
      if (safeInitialData.internalVoiceRecordings) {
        internalVoice.setRecordings(safeInitialData.internalVoiceRecordings);
      }
      if (safeInitialData.clientVoiceRecordings) {
        clientVoice.setRecordings(safeInitialData.clientVoiceRecordings);
      }
      
      // Reset save state
      hasUnsavedChangesRef.current = false;
      isSavingRef.current = false;
      setLastAutoSaved(null);
      setLastServerSync(null);
      setIsAutoSaving(false);
    }
  }, [isOpen, initialData]);

  // Gather current data for save (reads from refs for notes — zero re-render cost)
  const gatherCurrentData = useCallback(() => {
    return {
      internalNotes: internalNotesRef.current,
      clientNotes: clientNotesRef.current,
      internalVoiceRecordings: internalVoice.recordings,
      clientVoiceRecordings: clientVoice.recordings,
      clientExpectations,
      measurements,
      photos,
      sketches,
      siteConditions,
      customConditions,
      checklist,
      isChecklistComplete: checklist.every(item => !item.text || item.checked)
    };
  }, [internalVoice.recordings, clientVoice.recordings, 
      clientExpectations, measurements, photos, sketches, siteConditions, customConditions, checklist]);

  // Auto-save: local first, then try server. If server fails for ANY reason
  // (slow connection, timeout, error), enqueue to the offline queue so it
  // syncs reliably in the background. This is the fix for the
  // "autosave silently abandons data on slow connections" root cause.
  const performAutoSave = useCallback(async () => {
    if (!siteVisitId || !orgId || !hasUnsavedChangesRef.current) return;
    if (isSavingRef.current) return;
    isSavingRef.current = true;

    try {
      setIsAutoSaving(true);
      const data = gatherCurrentData();
      
      // 1. Always save locally first (instant, never fails, never blocks)
      const { saveFieldNotesDraft } = await import('@/lib/offlineSync');
      await saveFieldNotesDraft(siteVisitId, data, orgId);
      setLastAutoSaved(new Date());
      hasUnsavedChangesRef.current = false;
      
      // 2. Try server sync — onAutoSave is responsible for queueing on failure
      if (onAutoSave) {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();
        
        try {
          const timeoutId = setTimeout(() => {
            abortControllerRef.current?.abort();
          }, FETCH_TIMEOUT_MS);
          
          await onAutoSave(data);
          clearTimeout(timeoutId);
          
          setLastServerSync(new Date());
          // NOTE: We intentionally DO NOT clear the local draft here.
          // The parent's onAutoSave handler may only persist locally (it's
          // designed to be fast and offline-safe). Clearing the draft here
          // assumed onAutoSave always synced to the server — that
          // assumption was wrong and caused notes to evaporate seconds
          // after being typed. The draft is correctly cleared in two
          // places: (1) on a successful EXPLICIT save via handleSave,
          // (2) on page load when server data is newer than the draft.
        } catch (serverErr) {
          // Parent (SiteVisitDetailPage.handleSaveFieldNotes) already
          // enqueues to the offline queue on failure. Nothing else to do —
          // the draft AND the queue item both exist.
          if (serverErr.name === 'AbortError') {
            console.log('[AutoSave] Server sync timed out — queued for retry');
          } else {
            console.warn('[AutoSave] Server sync failed — queued for retry:', serverErr.message);
          }
        }
      }
    } catch (error) {
      console.error('[AutoSave] Local save failed:', error);
    } finally {
      setIsAutoSaving(false);
      isSavingRef.current = false;
    }
  }, [siteVisitId, orgId, gatherCurrentData, onAutoSave]);

  // Mark data as changed when non-text fields change (text fields mark via ref callbacks)
  useEffect(() => {
    if (!isOpen) return;
    hasUnsavedChangesRef.current = true;
  }, [clientExpectations, measurements, photos, sketches,
      siteConditions, customConditions, checklist, isOpen]);

  // Single stable auto-save timer (FIX 3: Performance - no re-creation on every change)
  useEffect(() => {
    if (!isOpen || !siteVisitId || !orgId) return;

    // Set up interval that checks ref (doesn't depend on changing state)
    const timer = setInterval(() => {
      if (hasUnsavedChangesRef.current) {
        performAutoSave();
      }
    }, AUTO_SAVE_INTERVAL);

    return () => clearInterval(timer);
  }, [isOpen, siteVisitId, orgId, performAutoSave]);

  // Save before closing (safety net)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChangesRef.current && isOpen) {
        performAutoSave();
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isOpen, performAutoSave]);

  // Get measurement suggestions based on service type
  // Niche-aware suggestions: merge primary + secondary niches' measurement objects
  const measurementSuggestions = useMemo(() => {
    const all = [];
    const seen = new Set();
    const addList = (list) => {
      for (const item of list || []) {
        if (!seen.has(item)) {
          seen.add(item);
          all.push(item);
        }
      }
    };
    // 1) explicit serviceType wins first
    addList(MEASUREMENT_SUGGESTIONS[serviceType]);
    // 2) primary + secondary niches contribute
    for (const n of nichePreset.allNiches || [nichePreset.niche]) {
      const key = NICHE_TO_SERVICE_KEY[n];
      if (key) addList(MEASUREMENT_SUGGESTIONS[key]);
    }
    // 3) always fall back to General as a safety net
    addList(MEASUREMENT_SUGGESTIONS['General']);
    return all;
  }, [serviceType, nichePreset]);

  // Handlers
  const addMeasurement = () => {
    setMeasurements(prev => [...prev, {
      id: Date.now(),
      label: '',
      customLabel: '',
      length: '',
      width: '',
      value: '',
      unit: 'ft',
      notes: ''
    }]);
  };

  const updateMeasurement = (id, field, val) => {
    setMeasurements(prev => prev.map(m => {
      if (m.id !== id) return m;
      const updated = { ...m, [field]: val };
      // Auto-calculate area when length and width are provided
      if ((field === 'length' || field === 'width') && updated.length && updated.width) {
        const l = parseFloat(updated.length);
        const w = parseFloat(updated.width);
        if (!isNaN(l) && !isNaN(w)) {
          updated.value = String(Math.round(l * w * 100) / 100);
        }
      }
      return updated;
    }));
  };

  const deleteMeasurement = (id) => {
    setMeasurements(prev => prev.filter(m => m.id !== id));
  };

  /**
   * Resolve the best <img src> for a stored photo, no matter where it came
   * from. Handles all four cases:
   *   - Freshly captured in this session → has `dataUrl` (data: URL, base64)
   *   - Successfully uploaded this session → has `url` (server path) AND `dataUrl`
   *   - Loaded from server on this/another device → has only `url`
   *   - Legacy string entry (old data) → the string itself is the URL
   * For server URLs we prepend REACT_APP_BACKEND_URL so the image works even
   * when the SPA is served from a different host than the API.
   */
  const resolvePhotoSrc = (photo) => {
    if (!photo) return '';
    if (typeof photo === 'string') {
      // legacy entry — could be a data URL or a server path
      return photo.startsWith('data:') ? photo
        : photo.startsWith('http') ? photo
        : `${process.env.REACT_APP_BACKEND_URL}${photo}`;
    }
    // Prefer the server URL when it exists AND looks like a server path
    // (the upload swap overwrites dataUrl with the server path too, so
    // either field can hold a /api/... URL).
    const candidate = photo.url || photo.dataUrl || '';
    if (!candidate) return '';
    if (candidate.startsWith('data:') || candidate.startsWith('http')) return candidate;
    return `${process.env.REACT_APP_BACKEND_URL}${candidate}`;
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    
    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Photo too large (max 10MB)');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = async (event) => {
        const localId = `local_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
        const dataUrl = event.target.result;
        
        // 1. Add to UI immediately (instant preview, optimistic)
        setPhotos(prev => [...prev, {
          id: localId,
          dataUrl,                  // for instant preview
          local_only: true,         // marker: still uploading in background
          name: file.name,
          timestamp: new Date().toISOString(),
          tag: ''
        }]);
        toast.success('Photo added');
        
        // 2. Queue background upload (decoupled from text save)
        try {
          const { queuePhotoUpload } = await import('@/lib/photoUploadManager');
          await queuePhotoUpload({
            siteVisitId,
            orgId,
            dataUrl,
            name: file.name,
            tag: '',
          });
          // Trigger immediate processor (best-effort; won't block UI)
          import('@/lib/photoUploadManager').then(({ processPhotoUploads }) => {
            processPhotoUploads().then((res) => {
              if (res.uploaded.length > 0) {
                // Update local state: swap base64 for server URL
                setPhotos(prev => prev.map(p => {
                  const match = res.uploaded.find(u => u.siteVisitId === siteVisitId);
                  if (match && p.local_only && p.name === file.name) {
                    return { ...p, dataUrl: match.serverUrl, url: match.serverUrl, server_id: match.serverId, local_only: false };
                  }
                  return p;
                }));
              }
            }).catch(() => { /* will retry in background */ });
          });
        } catch (err) {
          console.warn('[FieldNotes] Could not queue photo upload (will still save locally):', err);
        }
      };
      reader.readAsDataURL(file);
    });
    
    e.target.value = '';
  };

  const updatePhotoTag = (id, tag) => {
    setPhotos(prev => prev.map(p => 
      p.id === id ? { ...p, tag } : p
    ));
  };

  const deletePhoto = (id) => {
    setPhotos(prev => prev.filter(p => p.id !== id));
  };

  // Save photo to device / camera roll
  const savePhotoToDevice = async (photo) => {
    try {
      const src = resolvePhotoSrc(photo);
      if (!src) {
        toast.error('Photo file not available — try re-uploading');
        return;
      }
      // For server-hosted images we need to fetch + create an object URL
      // so the browser doesn't just navigate to the image; this also gives
      // us proper Content-Type which fixes the "unsupported file" issue
      // some users saw when downloading via raw <a href> on iOS.
      let downloadUrl = src;
      let revokeAfter = false;
      if (!src.startsWith('data:')) {
        try {
          const resp = await fetch(src, { credentials: 'omit' });
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          const blob = await resp.blob();
          downloadUrl = URL.createObjectURL(blob);
          revokeAfter = true;
        } catch (fetchErr) {
          console.warn('[FieldNotes] photo fetch failed, falling back to direct link:', fetchErr);
        }
      }
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `field_photo_${photo.id || Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      if (revokeAfter) setTimeout(() => URL.revokeObjectURL(downloadUrl), 500);
      toast.success('Photo saved to device');
    } catch (error) {
      console.error('Failed to save photo:', error);
      toast.error('Failed to save photo');
    }
  };

  // Save all photos to device
  const saveAllPhotosToDevice = () => {
    if (photos.length === 0) return;
    photos.forEach((photo, index) => {
      setTimeout(() => savePhotoToDevice(photo), index * 500);
    });
    toast.success(`Saving ${photos.length} photo(s) to device`);
  };

  const toggleSiteCondition = (id) => {
    setSiteConditions(prev => prev.map(c =>
      c.id === id ? { ...c, checked: !c.checked } : c
    ));
  };

  const updateSiteConditionNotes = (id, notes) => {
    setSiteConditions(prev => prev.map(c =>
      c.id === id ? { ...c, notes } : c
    ));
  };

  const addCustomCondition = () => {
    setCustomConditions(prev => [...prev, {
      id: Date.now(),
      label: '',
      checked: true,
      notes: ''
    }]);
  };

  const updateCustomCondition = (id, field, value) => {
    setCustomConditions(prev => prev.map(c =>
      c.id === id ? { ...c, [field]: value } : c
    ));
  };

  const deleteCustomCondition = (id) => {
    setCustomConditions(prev => prev.filter(c => c.id !== id));
  };

  const toggleChecklistItem = (id) => {
    setChecklist(prev => prev.map(item =>
      item.id === id ? { ...item, checked: !item.checked } : item
    ));
  };

  const addChecklistItem = () => {
    setChecklist(prev => [...prev, {
      id: Date.now().toString(),
      text: '',
      checked: false
    }]);
  };

  const updateChecklistText = (id, text) => {
    setChecklist(prev => prev.map(item =>
      item.id === id ? { ...item, text } : item
    ));
  };

  const deleteChecklistItem = (id) => {
    setChecklist(prev => prev.filter(item => item.id !== id));
  };

  // Check if all checklist items are completed
  const isChecklistComplete = checklist.every(item => !item.text || item.checked);
  const checklistProgress = checklist.filter(item => item.checked).length;

  const handleSave = async () => {
    const data = {
      internalNotes: internalNotesRef.current,
      clientNotes: clientNotesRef.current,
      internalVoiceRecordings: internalVoice.recordings,
      clientVoiceRecordings: clientVoice.recordings,
      clientExpectations,
      measurements,
      photos,
      sketches,
      siteConditions,
      customConditions,
      checklist,
      isChecklistComplete
    };

    try {
      // Await the parent's save handler — it manages toasts, drafts, and offline queue.
      // If onSave throws (FieldNotesPage rejects on server failure), keep the editor
      // open so the user can see the SaveStatusBadge + tap Retry without losing context.
      await onSave(data);
      hasUnsavedChangesRef.current = false;
      onClose();
    } catch (err) {
      // Parent's onSave already toasted; just stay on the editor.
      console.warn('[FieldNotes] save failed, staying on editor:', err);
    }
  };

  // Save FIRST, then call the completion handler. We do NOT auto-close so the
  // user sees the success toast in context and the snapshot banner appears.
  const handleSaveAndComplete = async () => {
    const data = {
      internalNotes: internalNotesRef.current,
      clientNotes: clientNotesRef.current,
      internalVoiceRecordings: internalVoice.recordings,
      clientVoiceRecordings: clientVoice.recordings,
      clientExpectations,
      measurements,
      photos,
      sketches,
      siteConditions,
      customConditions,
      checklist,
      isChecklistComplete,
    };
    try {
      // Persist latest edits, but do not close on save.
      await onSave(data, { skipClose: true });
      hasUnsavedChangesRef.current = false;
    } catch (err) {
      // Parent's onSave already toasted; bail out so we don't snapshot a stale state.
      console.warn('[FieldNotes] save before complete failed:', err);
      return;
    }
    if (onMarkComplete) {
      await onMarkComplete();
    }
  };

  // Swipe-to-dismiss functionality for mobile
  const handleDrawerClose = useCallback(async () => {
    // If there are unsaved changes, auto-save to server before closing
    if (hasUnsavedChangesRef.current) {
      await performAutoSave();
    }
    onClose();
  }, [onClose, performAutoSave]);
  
  const { handlers: swipeHandlers, style: swipeStyle, swipeProgress } = useSwipeToDismiss(handleDrawerClose, {
    threshold: 120,
    direction: 'right',
    enabled: true,
  });

  if (!isOpen) return null;

  const isPageMode = mode === 'page';
  // Use Portal to render at document body level, avoiding stacking context issues.
  // In page mode, render inline (no portal, no overlay) so it acts as a real page.
  const wrapper = (
    <>
      {/* Backdrop — only in drawer mode */}
      {!isPageMode && (
        <div 
          className="fixed inset-0 bg-black/50 z-[9998] transition-opacity"
          style={{ opacity: 1 - (swipeProgress * 0.5) }}
          onClick={handleDrawerClose}
        />
      )}
      
      {/* Drawer / Page */}
      <div 
        className={isPageMode
          ? "bg-gray-50 min-h-screen flex flex-col w-full"
          : "fixed inset-y-0 right-0 w-full max-w-2xl bg-white shadow-2xl z-[9999] flex flex-col"}
        {...(isPageMode ? {} : swipeHandlers)}
        style={isPageMode ? {} : { ...swipeStyle, paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        {/* Swipe indicator — drawer mode only */}
        {!isPageMode && (
          <div className="sm:hidden absolute left-2 top-1/2 -translate-y-1/2 flex flex-col items-center gap-1 opacity-30 pointer-events-none">
            <div className="w-1 h-8 bg-gray-400 rounded-full" />
            <span className="text-[10px] text-gray-500 rotate-90 whitespace-nowrap">swipe to close</span>
          </div>
        )}
        
        {/* Header — slim native style in page mode, colored block in drawer mode */}
        {isPageMode ? (
          <div className="sticky top-0 z-30 bg-white border-b border-gray-200 px-3 sm:px-5 py-3 flex items-center gap-3" data-testid="field-notes-page-header">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDrawerClose}
              className="h-9 w-9 -ml-1 text-gray-700 hover:bg-gray-100"
              data-testid="field-notes-back"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-base sm:text-lg font-bold leading-tight text-gray-900 truncate">Field Notes</h1>
              {saveStatus !== null ? (
                <div className="mt-0.5">
                  <SaveStatusBadge
                    status={saveStatus}
                    lastSavedAt={saveLastSavedAt}
                    lastError={saveLastError}
                    onRetry={onRetrySave}
                    developerMode={developerMode}
                  />
                </div>
              ) : isAutoSaving ? (
                <p className="text-xs text-amber-600 flex items-center gap-1.5 mt-0.5">
                  <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" />
                  Syncing…
                </p>
              ) : lastServerSync ? (
                <p className="text-xs text-emerald-600 flex items-center gap-1.5 mt-0.5">
                  <Check className="w-3 h-3" />
                  Synced {lastServerSync.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              ) : lastAutoSaved ? (
                <p className="text-xs text-gray-500 flex items-center gap-1.5 mt-0.5">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full" />
                  Saved locally
                </p>
              ) : (
                <p className="text-xs text-gray-500 truncate">{serviceType}</p>
              )}
            </div>
            <Button
              onClick={handleSave}
              size="sm"
              className="bg-green-600 hover:bg-green-700 text-white h-9 px-4 shadow-sm"
              data-testid="field-notes-save-top"
            >
              <Save className="w-4 h-4 mr-1.5" />
              Save
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between p-3 sm:p-4 pt-[max(0.75rem,env(safe-area-inset-top))] sm:pt-4 border-b border-gray-200 bg-primary text-white">
            <div className="flex items-center gap-2 sm:gap-3">
              <StickyNote className="w-5 h-5 sm:w-6 sm:h-6" />
              <div>
                <h2 className="font-heading text-lg sm:text-xl font-bold">Field Notes</h2>
                {isAutoSaving ? (
                  <p className="text-xs opacity-80 flex items-center gap-1">
                    <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
                    Syncing...
                  </p>
                ) : lastServerSync ? (
                  <p className="text-xs opacity-80 flex items-center gap-1" data-testid="sync-status">
                    <Check className="w-3 h-3 text-green-300" />
                    Synced {lastServerSync.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                ) : lastAutoSaved ? (
                  <p className="text-xs opacity-80 flex items-center gap-1" data-testid="sync-status">
                    <span className="w-2 h-2 bg-amber-400 rounded-full" />
                    Saved locally {lastAutoSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                ) : (
                  <p className="text-xs sm:text-sm opacity-80">{serviceType} Site Visit</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Save button in header for mobile visibility */}
              <Button
                onClick={handleSave}
                size="sm"
                className="sm:hidden bg-green-600 hover:bg-green-700 text-white h-9 px-3"
                data-testid="field-notes-save-mobile"
              >
                <Save className="w-4 h-4 mr-1" />
                Save
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleDrawerClose}
                className="text-white hover:bg-white/20 h-9 w-9"
                data-testid="field-notes-close"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </Button>
            </div>
          </div>
        )}
        
        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          
          {/* 1. NOTES SECTION */}
          <CollapsibleSection title="Notes" icon={MessageSquare} defaultOpen={true}>
            {/* Internal Notes */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Internal Notes</Label>
                <span className="text-xs text-muted-foreground">Team only</span>
              </div>
              
              {/* Voice Recording for Internal */}
              <div className="flex gap-2">
                {internalVoice.isSupported && (
                  <Button
                    onClick={internalVoice.isRecording ? internalVoice.stopRecording : internalVoice.startRecording}
                    variant={internalVoice.isRecording ? 'destructive' : 'outline'}
                    size="sm"
                    className={`h-10 ${internalVoice.isRecording ? 'animate-pulse' : ''}`}
                  >
                    {internalVoice.isRecording ? (
                      <><Square className="w-4 h-4 mr-2" /> Stop</>
                    ) : (
                      <><Mic className="w-4 h-4 mr-2" /> Voice Note</>
                    )}
                  </Button>
                )}
              </div>
              
              {/* Voice Recordings List */}
              {internalVoice.recordings.length > 0 && (
                <div className="space-y-2">
                  {internalVoice.recordings.map(recording => (
                    <AudioPlayer 
                      key={recording.id} 
                      recording={recording} 
                      onDelete={internalVoice.deleteRecording}
                    />
                  ))}
                </div>
              )}
              
              <UncontrolledTextarea
                key={`internal-${reopenKey}`}
                defaultValue={initialDataRef.current.internalNotes || ''}
                onChangeRef={onInternalNotesChange}
                placeholder="Notes for your team only (not shared with client)..."
                className="min-h-[100px]"
                testId="internal-notes-input"
              />
            </div>
            
            {/* Client Notes */}
            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Client Notes</Label>
                <span className="text-xs text-muted-foreground">Can be shared</span>
              </div>
              
              {/* Voice Recording for Client */}
              <div className="flex gap-2">
                {clientVoice.isSupported && (
                  <Button
                    onClick={clientVoice.isRecording ? clientVoice.stopRecording : clientVoice.startRecording}
                    variant={clientVoice.isRecording ? 'destructive' : 'outline'}
                    size="sm"
                    className={`h-10 ${clientVoice.isRecording ? 'animate-pulse' : ''}`}
                  >
                    {clientVoice.isRecording ? (
                      <><Square className="w-4 h-4 mr-2" /> Stop</>
                    ) : (
                      <><Mic className="w-4 h-4 mr-2" /> Voice Note</>
                    )}
                  </Button>
                )}
              </div>
              
              {/* Voice Recordings List */}
              {clientVoice.recordings.length > 0 && (
                <div className="space-y-2">
                  {clientVoice.recordings.map(recording => (
                    <AudioPlayer 
                      key={recording.id} 
                      recording={recording} 
                      onDelete={clientVoice.deleteRecording}
                    />
                  ))}
                </div>
              )}
              
              <UncontrolledTextarea
                key={`client-${reopenKey}`}
                defaultValue={initialDataRef.current.clientNotes || ''}
                onChangeRef={onClientNotesChange}
                placeholder="Notes that can be included in the estimate..."
                className="min-h-[100px]"
                testId="client-notes-input"
              />
            </div>
          </CollapsibleSection>

          {/* 2. CLIENT EXPECTATIONS */}
          <CollapsibleSection title="Client Expectations" icon={Users} defaultOpen={false}>
            <div className="space-y-4">
              <div>
                <Label>What the client wants done</Label>
                <DebouncedTextarea
                  value={clientExpectations.wantsDone}
                  onChange={(val) => setClientExpectations(prev => ({ ...prev, wantsDone: val }))}
                  placeholder="Describe what the client is looking to accomplish..."
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Timeline expectations</Label>
                <DebouncedInput
                  value={clientExpectations.timeline}
                  onChange={(val) => setClientExpectations(prev => ({ ...prev, timeline: val }))}
                  placeholder="e.g., Before summer, Within 2 weeks, ASAP..."
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Budget range (optional)</Label>
                <Select
                  value={clientExpectations.budgetRange}
                  onValueChange={(value) => setClientExpectations(prev => ({ ...prev, budgetRange: value }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select budget range" />
                  </SelectTrigger>
                  <SelectContent className="z-[10001]">
                    {BUDGET_RANGES.map(range => (
                      <SelectItem key={range.value} value={range.value || "not_discussed"}>
                        {range.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Special requests</Label>
                <DebouncedTextarea
                  value={clientExpectations.specialRequests}
                  onChange={(val) => setClientExpectations(prev => ({ ...prev, specialRequests: val }))}
                  placeholder="Any specific requirements, preferences, or concerns..."
                  className="mt-1"
                />
              </div>
            </div>
          </CollapsibleSection>

          {/* 3. MEASUREMENTS */}
          <CollapsibleSection 
            title="Measurements" 
            icon={Ruler} 
            defaultOpen={false}
            badge={measurements.length > 0 ? measurements.length : null}
          >
            <div className="space-y-3">
              {measurements.map((measurement) => (
                <div key={measurement.id} className="p-3 bg-gray-50 rounded-lg space-y-2" data-testid={`measurement-${measurement.id}`}>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Select
                        value={measurement.label}
                        onValueChange={(value) => updateMeasurement(measurement.id, 'label', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="What are you measuring?" />
                        </SelectTrigger>
                        <SelectContent className="z-[10001]">
                          {measurementSuggestions.map(suggestion => (
                            <SelectItem key={suggestion} value={suggestion}>
                              {suggestion}
                            </SelectItem>
                          ))}
                          <SelectItem value="custom">Custom...</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <Select
                      value={measurement.unit}
                      onValueChange={(value) => updateMeasurement(measurement.id, 'unit', value)}
                    >
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[10001]">
                        {MEASUREMENT_UNITS.map(unit => (
                          <SelectItem key={unit.value} value={unit.value}>
                            {unit.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteMeasurement(measurement.id)}
                      className="text-muted-foreground hover:text-destructive flex-shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {measurement.label === 'custom' && (
                    <DebouncedInput
                      value={measurement.customLabel || ''}
                      onChange={(val) => updateMeasurement(measurement.id, 'customLabel', val)}
                      placeholder="e.g. Front patio, Pool deck, Crawl space…"
                    />
                  )}
                  
                  <p className="text-[11px] text-muted-foreground italic px-0.5">
                    Fill any combination — length only, width only, area only, or all three. Area auto-calculates from length × width.
                  </p>

                  {/* Dimensions: Length × Width → Area (any field optional) */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <DebouncedInput
                        type="number"
                        inputMode="decimal"
                        value={measurement.length || ''}
                        onChange={(val) => updateMeasurement(measurement.id, 'length', val)}
                        placeholder="Length"
                        className="text-center"
                        data-testid={`measurement-length-${measurement.id}`}
                      />
                    </div>
                    <span className="text-muted-foreground font-bold">x</span>
                    <div className="flex-1">
                      <DebouncedInput
                        type="number"
                        inputMode="decimal"
                        value={measurement.width || ''}
                        onChange={(val) => updateMeasurement(measurement.id, 'width', val)}
                        placeholder="Width"
                        className="text-center"
                        data-testid={`measurement-width-${measurement.id}`}
                      />
                    </div>
                    <span className="text-muted-foreground font-bold">=</span>
                    <div className="flex-1">
                      <DebouncedInput
                        type="number"
                        inputMode="decimal"
                        value={measurement.value || ''}
                        onChange={(val) => updateMeasurement(measurement.id, 'value', val)}
                        placeholder="Area"
                        className="text-center font-semibold"
                        data-testid={`measurement-area-${measurement.id}`}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground w-12">
                      sq {measurement.unit}
                    </span>
                  </div>
                  
                  {/* Optional notes per measurement */}
                  <DebouncedInput
                    value={measurement.notes || ''}
                    onChange={(val) => updateMeasurement(measurement.id, 'notes', val)}
                    placeholder="Notes (optional, e.g. 'sloped', 'gravel base')"
                    className="text-sm"
                  />
                </div>
              ))}
              
              <Button
                variant="outline"
                onClick={addMeasurement}
                className="w-full"
                data-testid="add-measurement-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Measurement
              </Button>
            </div>
          </CollapsibleSection>

          {/* 4. PHOTOS */}
          <CollapsibleSection 
            title="Photos" 
            icon={Camera} 
            defaultOpen={photos.length > 0}
            badge={photos.length > 0 ? photos.length : null}
          >
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={() => setCameraOpen(true)}
                  className="h-14"
                  data-testid="field-notes-take-photo"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  Take Photo
                </Button>
                <Button
                  variant="outline"
                  onClick={() => uploadInputRef.current?.click()}
                  className="h-14"
                  data-testid="field-notes-upload-photo"
                >
                  <ImagePlus className="w-5 h-5 mr-2" />
                  Upload
                </Button>
              </div>
              {photos.length > 0 && (
                <Button
                  variant="outline"
                  onClick={saveAllPhotosToDevice}
                  className="w-full h-10"
                  title="Save all photos to device"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Save all to device
                </Button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                onChange={handlePhotoUpload}
                className="hidden"
              />
              <input
                ref={uploadInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
                className="hidden"
              />
              <CameraCaptureModal
                open={cameraOpen}
                onClose={() => setCameraOpen(false)}
                onCapture={handlePhotoUpload}
              />
              
              {photos.length > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  {photos.map((photo) => {
                    const src = resolvePhotoSrc(photo);
                    return (
                    <div key={photo.id} className="relative group">
                      {src ? (
                        <img
                          src={src}
                          alt={photo.name || 'Photo'}
                          className="w-full h-32 object-cover rounded-lg border border-gray-200 cursor-pointer"
                          loading="lazy"
                          onClick={() => window.open(src, '_blank', 'noopener,noreferrer')}
                          onError={(e) => {
                            // Hide the broken img and reveal the fallback below
                            e.currentTarget.style.display = 'none';
                            const sib = e.currentTarget.nextElementSibling;
                            if (sib) sib.style.display = 'flex';
                          }}
                          data-testid={`field-photo-${photo.id}`}
                        />
                      ) : null}
                      <div
                        className="w-full h-32 rounded-lg border border-dashed border-amber-300 bg-amber-50 text-amber-800 flex-col items-center justify-center text-center px-2"
                        style={{ display: src ? 'none' : 'flex' }}
                        data-testid={`field-photo-missing-${photo.id}`}
                      >
                        <AlertTriangle className="w-5 h-5 mb-1" />
                        <span className="text-[11px] leading-tight">Photo unavailable<br/>Re-upload required</span>
                      </div>
                      
                      {/* Tag Badge */}
                      {photo.tag && (
                        <Badge className={`absolute top-2 left-2 ${PHOTO_TAGS.find(t => t.value === photo.tag)?.color}`}>
                          {PHOTO_TAGS.find(t => t.value === photo.tag)?.label}
                        </Badge>
                      )}
                      
                      {/* Actions - Download and Delete (always visible on mobile, hover on desktop) */}
                      <div className="absolute top-2 right-2 flex gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="secondary"
                          size="icon"
                          className="h-8 w-8 bg-white/90 shadow-sm"
                          onClick={() => savePhotoToDevice(photo)}
                          title="Save to device"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="icon"
                          className="h-8 w-8 shadow-sm"
                          onClick={() => deletePhoto(photo.id)}
                          data-testid={`delete-photo-${photo.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      {/* Tag Selector */}
                      <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent rounded-b-lg">
                        <Select
                          value={photo.tag}
                          onValueChange={(value) => updatePhotoTag(photo.id, value)}
                        >
                          <SelectTrigger className="h-8 bg-white/90 text-xs">
                            <SelectValue placeholder="Add tag" />
                          </SelectTrigger>
                          <SelectContent className="z-[10001]">
                            {nichePreset.photoTags.map(tag => (
                              <SelectItem key={tag.value} value={tag.value}>
                                {tag.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                  <ImagePlus className="w-12 h-12 text-muted-foreground mx-auto mb-2 opacity-50" />
                  <p className="text-muted-foreground">No photos yet</p>
                </div>
              )}
            </div>
          </CollapsibleSection>

          {/* 5. SKETCHES / DRAWINGS */}
          <CollapsibleSection 
            title="Sketches" 
            icon={Pen} 
            defaultOpen={false}
            badge={sketches.length > 0 ? sketches.length : null}
          >
            <FieldNotesSketchPad
              sketches={sketches}
              onSave={(dataUrl) => setSketches(prev => [...prev, dataUrl])}
              onDelete={(index) => setSketches(prev => prev.filter((_, i) => i !== index))}
            />
          </CollapsibleSection>

          {/* 6. SITE CONDITIONS */}
          <CollapsibleSection title="Site Conditions" icon={AlertTriangle} defaultOpen={false}>
            <div className="space-y-3">
              {siteConditions.map((condition) => (
                <div key={condition.id} className="space-y-2">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id={condition.id}
                      checked={condition.checked}
                      onCheckedChange={() => toggleSiteCondition(condition.id)}
                    />
                    <Label htmlFor={condition.id} className="cursor-pointer flex-1">
                      {condition.label}
                    </Label>
                  </div>
                  
                  {condition.checked && (
                    <DebouncedInput
                      value={condition.notes}
                      onChange={(val) => updateSiteConditionNotes(condition.id, val)}
                      placeholder="Add details (optional)..."
                      className="ml-7"
                    />
                  )}
                </div>
              ))}
              
              {/* Custom Conditions */}
              {customConditions.map((condition) => (
                <div key={condition.id} className="space-y-2 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <DebouncedInput
                      value={condition.label}
                      onChange={(val) => updateCustomCondition(condition.id, 'label', val)}
                      placeholder="Custom condition..."
                      className="flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteCustomCondition(condition.id)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <DebouncedInput
                    value={condition.notes}
                    onChange={(val) => updateCustomCondition(condition.id, 'notes', val)}
                    placeholder="Add details..."
                  />
                </div>
              ))}
              
              <Button
                variant="outline"
                onClick={addCustomCondition}
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Custom Condition
              </Button>
            </div>
          </CollapsibleSection>

          {/* 7. CHECKLIST */}
          <CollapsibleSection 
            title="Checklist" 
            icon={ClipboardCheck} 
            defaultOpen={false}
            badge={`${checklistProgress}/${checklist.length}`}
          >
            <div className="space-y-2">
              {!isChecklistComplete && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  Complete all checklist items before marking the visit as complete.
                </div>
              )}
              
              {checklist.map((item) => (
                <div key={item.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <button
                    onClick={() => toggleChecklistItem(item.id)}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      item.checked 
                        ? 'bg-green-500 text-white' 
                        : 'bg-white border-2 border-gray-300'
                    }`}
                  >
                    {item.checked && <Check className="w-5 h-5" />}
                  </button>
                  <DebouncedInput
                    multiline
                    value={item.text}
                    onChange={(val) => updateChecklistText(item.id, val)}
                    placeholder="Checklist item..."
                    className={`flex-1 min-w-0 border-0 bg-transparent resize-none whitespace-pre-wrap break-words text-sm leading-relaxed ${item.checked ? 'line-through text-muted-foreground' : ''}`}
                    rows={1}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteChecklistItem(item.id)}
                    className="text-muted-foreground hover:text-destructive flex-shrink-0 mt-0.5"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              
              <Button
                variant="outline"
                onClick={addChecklistItem}
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </div>
          </CollapsibleSection>
        </div>
        
        {/* Footer - sticky save bar (always visible) + scroll-to-top FAB on page mode */}
        <div className="p-4 border-t border-gray-200 bg-white sticky bottom-0 shadow-[0_-4px_12px_rgba(0,0,0,0.04)] space-y-2" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
          <Button
            onClick={handleSave}
            className="w-full h-14 text-lg font-bold bg-green-600 hover:bg-green-700 shadow-md"
            data-testid="field-notes-save-bottom"
          >
            <Save className="w-5 h-5 mr-2" />
            Save Field Notes
          </Button>
          {onMarkComplete && (
            <Button
              onClick={handleSaveAndComplete}
              disabled={!isChecklistComplete && checklist.some(i => i.text)}
              variant="outline"
              className="w-full h-12 text-base font-semibold border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
              data-testid="field-notes-mark-complete"
            >
              <Check className="w-5 h-5 mr-2" />
              {isCompleted ? 'Re-snapshot Site Visit' : 'Mark Site Visit Complete'}
            </Button>
          )}
          {onMarkComplete && !isChecklistComplete && checklist.some(i => i.text) && (
            <p className="text-xs text-amber-700 text-center">
              Finish every checklist item to enable completion.
            </p>
          )}
        </div>

        {/* Scroll-to-top FAB — page mode only, appears after scroll */}
        {isPageMode && showScrollTop && (
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-24 right-4 w-11 h-11 rounded-full bg-gray-900/85 backdrop-blur text-white shadow-lg flex items-center justify-center z-40 hover:bg-gray-900 transition-all"
            data-testid="field-notes-scroll-top"
            aria-label="Scroll to top"
          >
            <ChevronUp className="w-5 h-5" />
          </button>
        )}
      </div>
    </>
  );

  return isPageMode ? wrapper : createPortal(wrapper, document.body);
};

export default SiteVisitFieldNotes;
